import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import EstadisticasEvaluaciones from './EstadisticasEvaluaciones';

const RRHHDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [user, setUser] = useState(null);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [stats, setStats] = useState({
    totalEvaluaciones: 0,
    completadas: 0,
    pendientes: 0,
    empleadosCompletados: [],
    empleadosPendientes: [],
    evaluacionesPorArea: {}
  });
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        loadUserData(firebaseUser);
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadUserData = async (user) => {
    try {
      console.log("🔍 RRHH - Buscando datos del usuario:", user.uid);
      
      // Buscar datos del usuario RRHH
      const q = query(collection(db, "usuarios"), where("uid", "==", user.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        console.log("🏢 RRHH - Empresa del usuario:", userData.empresa_id || userData.empresaId || userData.empresa || userData.nombreEmpresa || "NO ENCONTRADA");
        
        // Si tenemos empresaId, buscar el nombre de la empresa en la colección empresas
        if (userData.empresaId) {
          try {
            const empresasQuery = query(
              collection(db, "empresas"),
              where("__name__", "==", userData.empresaId)
            );
            const empresasSnapshot = await getDocs(empresasQuery);
            
            if (!empresasSnapshot.empty) {
              // Obtener el nombre de la empresa
              const empresaData = empresasSnapshot.docs[0].data();
              if (empresaData.nombre) {
                userData.empresaNombre = empresaData.nombre;
                console.log("✅ RRHH - Nombre de empresa obtenido:", empresaData.nombre);
              }
            }
          } catch (error) {
            console.log("⚠️ RRHH - Error obteniendo nombre de empresa:", error);
          }
        }
        
        // Actualizar el estado con la información completa
        setUserData({...userData});
        
        // Intentar con diferentes campos de empresa (empresaId es el correcto)
        const empresaId = userData.empresaId || userData.empresa_id || userData.empresa || userData.nombreEmpresa;
        if (empresaId) {
          console.log("🎯 RRHH - Cargando estadísticas para empresa ID:", empresaId);
          await loadEvaluacionesStats(empresaId);
        } else {
          console.log("❌ RRHH - No se encontró ID de empresa en ningún campo");
          console.log("🔍 RRHH - Campos disponibles en userData:", Object.keys(userData));
        }
      } else {
        console.log("❌ RRHH - No se encontraron datos del usuario");
      }
      setLoading(false);
    } catch (error) {
      console.error("❌ RRHH - Error cargando datos del usuario:", error);
      setLoading(false);
    }
  };

  const loadEvaluacionesStats = async (empresaId) => {
    try {
      console.log("📊 RRHH - Cargando evaluaciones para empresa:", empresaId);
      
      // Cargar evaluaciones
      const evaluacionesQuery = query(
        collection(db, "evaluaciones_asignadas"),
        where("empresa_id", "==", empresaId)
      );
      const evaluacionesSnapshot = await getDocs(evaluacionesQuery);
      const evaluacionesData = evaluacionesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("📋 RRHH - Evaluaciones encontradas:", evaluacionesData.length);
      setEvaluaciones(evaluacionesData);

      // Cargar empleados para obtener departamentos
      const empleadosQuery = query(
        collection(db, "empleados"),
        where("empresa_id", "==", empresaId)
      );
      const empleadosSnapshot = await getDocs(empleadosQuery);
      const empleadosData = empleadosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("👥 RRHH - Empleados encontrados:", empleadosData.length);
      setEmpleados(empleadosData);

      // Crear un mapeo de cédula a departamento
      const departamentoPorCedula = {};
      empleadosData.forEach(empleado => {
        if (empleado.cedula && empleado.departamento) {
          departamentoPorCedula[empleado.cedula] = empleado.departamento;
        }
      });

      console.log("🏢 RRHH - Mapeo de departamentos:", departamentoPorCedula);

      // Procesar estadísticas
      const completadas = [];
      const pendientes = [];
      const evaluacionesPorArea = {};

      evaluacionesData.forEach(evaluacion => {
        const departamento = departamentoPorCedula[evaluacion.empleadoCedula] || 'Sin Área';
        
        // Inicializar área si no existe
        if (!evaluacionesPorArea[departamento]) {
          evaluacionesPorArea[departamento] = {
            total: 0,
            completadas: 0,
            pendientes: 0
          };
        }
        
        evaluacionesPorArea[departamento].total++;

        if (evaluacion.estado === 'completada') {
          completadas.push({
            ...evaluacion,
            departamento: departamento
          });
          evaluacionesPorArea[departamento].completadas++;
        } else {
          pendientes.push({
            ...evaluacion,
            departamento: departamento
          });
          evaluacionesPorArea[departamento].pendientes++;
        }
      });

      console.log("📈 RRHH - Evaluaciones por área:", evaluacionesPorArea);

      setStats({
        totalEvaluaciones: evaluacionesData.length,
        completadas: completadas.length,
        pendientes: pendientes.length,
        empleadosCompletados: completadas,
        empleadosPendientes: pendientes,
        evaluacionesPorArea: evaluacionesPorArea
      });

    } catch (error) {
      console.error("❌ RRHH - Error cargando estadísticas:", error);
    }
  };

  const renderDashboard = () => {
    const areasConEjecucion = Object.entries(stats.evaluacionesPorArea)
      .filter(([area, data]) => data.completadas > 0)
      .map(([area, data]) => ({
        area,
        porcentaje: Math.round((data.completadas / data.total) * 100)
      }))
      .sort((a, b) => b.porcentaje - a.porcentaje);

    return (
      <div style={{ padding: '2rem' }}>
        {/* Estadísticas con componente compartido */}
        <EstadisticasEvaluaciones userData={userData} tipo="rrhh" />

        {/* Gráficos lado a lado */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '2rem',
          marginBottom: '2rem'
        }}>
          {/* Gráfico de Torta - Estado General */}
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ 
              margin: '0 0 1.5rem 0', 
              color: '#1e293b', 
              fontSize: '1.25rem',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              📊 Estado General de Evaluaciones
            </h3>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem' }}>
              {/* Gráfico de torta */}
              <div style={{ position: 'relative' }}>
                {stats.totalEvaluaciones > 0 ? (
                  <div
                    style={{
                      width: '180px',
                      height: '180px',
                      borderRadius: '50%',
                      background: `conic-gradient(
                        from 0deg,
                        #22c55e 0deg ${(stats.completadas / stats.totalEvaluaciones) * 360}deg,
                        #f59e0b ${(stats.completadas / stats.totalEvaluaciones) * 360}deg 360deg
                      )`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                    }}
                  >
                    <div style={{
                      width: '120px',
                      height: '120px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e293b' }}>
                        {Math.round((stats.completadas / stats.totalEvaluaciones) * 100)}%
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>
                        Completado
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: '180px',
                    height: '180px',
                    borderRadius: '50%',
                    background: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    fontSize: '0.875rem'
                  }}>
                    Sin datos
                  </div>
                )}
              </div>

              {/* Leyenda */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#22c55e',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(34, 197, 94, 0.3)'
                  }}></div>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1e293b' }}>
                      Completadas
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#22c55e' }}>
                      {stats.completadas}
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#f59e0b',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                  }}></div>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1e293b' }}>
                      Pendientes
                    </div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#f59e0b' }}>
                      {stats.pendientes}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de Barras - Áreas con Ejecución */}
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ 
              margin: '0 0 1.5rem 0', 
              color: '#1e293b', 
              fontSize: '1.25rem',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              📈 Ejecución por Área
            </h3>
            
            {areasConEjecucion.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {areasConEjecucion.slice(0, 6).map((item, index) => (
                  <div key={item.area} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1e293b' }}>
                        {item.area}
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#3b82f6' }}>
                        {item.porcentaje}%
                      </span>
                    </div>
                    <div style={{ 
                      width: '100%', 
                      height: '8px', 
                      backgroundColor: '#f1f5f9', 
                      borderRadius: '4px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: `${item.porcentaje}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, 
                          ${index === 0 ? '#10b981' : index === 1 ? '#3b82f6' : index === 2 ? '#8b5cf6' : '#f59e0b'} 0%, 
                          ${index === 0 ? '#059669' : index === 1 ? '#1d4ed8' : index === 2 ? '#7c3aed' : '#d97706'} 100%
                        )`,
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                        animation: 'shimmer 2s infinite'
                      }}>
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                          animation: 'shimmer 2s infinite'
                        }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                color: '#64748b', 
                fontSize: '0.875rem',
                padding: '2rem 0'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                <p>No hay áreas con evaluaciones completadas</p>
              </div>
            )}
          </div>
        </div>

        {/* Resumen de áreas */}
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{ 
            margin: '0 0 1.5rem 0', 
            color: '#1e293b', 
            fontSize: '1.25rem',
            fontWeight: '600'
          }}>
            🏢 Resumen por Área
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: '1rem'
          }}>
            {Object.entries(stats.evaluacionesPorArea).map(([area, data]) => (
              <div key={area} style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1rem' }}>
                  {area}
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Total:</span>
                  <span style={{ fontWeight: 'bold', color: '#1e293b' }}>{data.total}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#22c55e', fontSize: '0.875rem' }}>Completadas:</span>
                  <span style={{ fontWeight: 'bold', color: '#22c55e' }}>{data.completadas}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#f59e0b', fontSize: '0.875rem' }}>Pendientes:</span>
                  <span style={{ fontWeight: 'bold', color: '#f59e0b' }}>{data.pendientes}</span>
                </div>
                <div style={{ 
                  marginTop: '1rem',
                  padding: '0.5rem',
                  background: data.completadas === data.total ? '#dcfce7' : data.completadas === 0 ? '#fef3c7' : '#dbeafe',
                  borderRadius: '6px',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: data.completadas === data.total ? '#166534' : data.completadas === 0 ? '#92400e' : '#1e40af'
                }}>
                  {data.total > 0 ? Math.round((data.completadas / data.total) * 100) : 0}% Completado
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCompletadas = () => {
    // Agrupar evaluaciones completadas por departamento
    const evaluacionesPorDepartamento = {};
    stats.empleadosCompletados.forEach(evaluacion => {
      const departamento = evaluacion.departamento || 'Sin Área';
      if (!evaluacionesPorDepartamento[departamento]) {
        evaluacionesPorDepartamento[departamento] = [];
      }
      evaluacionesPorDepartamento[departamento].push(evaluacion);
    });

    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h2 style={{ color: '#1e293b', fontSize: '1.5rem', margin: '0 0 0.5rem 0' }}>
            ✅ Evaluaciones Completadas
          </h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: '1rem' }}>
            {stats.completadas} evaluaciones completadas de {stats.totalEvaluaciones} totales
          </p>
        </div>

        {Object.keys(evaluacionesPorDepartamento).length > 0 ? (
          Object.entries(evaluacionesPorDepartamento).map(([departamento, evaluacionesDept]) => (
            <div key={departamento} style={{ marginBottom: '2rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '12px 12px 0 0',
                marginBottom: '1rem'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                  🏢 {departamento} ({evaluacionesDept.length} evaluaciones)
                </h3>
              </div>

              <div style={{ 
                display: 'grid', 
                gap: '1rem',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))'
              }}>
                {evaluacionesDept.map(evaluacion => {
                  const fechaCreacion = evaluacion.fechaCreacion ? new Date(evaluacion.fechaCreacion.seconds * 1000) : null;
                  
                  return (
                    <div key={evaluacion.id} style={{
                      background: 'white',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                      border: '1px solid #e5e7eb',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Indicador de completada */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: '#22c55e',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '0 12px 0 12px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        ✅ COMPLETADA
                      </div>

                      <div style={{ paddingTop: '1rem' }}>
                        <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.1rem' }}>
                          👤 {evaluacion.empleadoNombre}
                        </h4>
                        
                        <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>📋 Evaluación:</span>
                            <span style={{ fontWeight: '500', color: '#1e293b' }}>
                              {evaluacion.evaluacionNombre || 'No especificado'}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>🆔 Cédula:</span>
                            <span style={{ fontWeight: '500', color: '#1e293b' }}>
                              {evaluacion.empleadoCedula}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>🏢 Área:</span>
                            <span style={{ fontWeight: '500', color: '#3b82f6' }}>
                              {evaluacion.departamento}
                            </span>
                          </div>
                          
                          {fechaCreacion && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#64748b' }}>📅 Fecha:</span>
                              <span style={{ fontWeight: '500', color: '#22c55e' }}>
                                {fechaCreacion.toLocaleDateString('es-ES')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div style={{
            background: 'white',
            padding: '3rem',
            borderRadius: '16px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#374151' }}>
              Aún no hay evaluaciones completadas
            </h3>
            <p style={{ fontSize: '1rem', color: '#6b7280' }}>
              Las evaluaciones completadas aparecerán aquí
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderPendientes = () => {
    // Agrupar evaluaciones pendientes por departamento
    const evaluacionesPorDepartamento = {};
    stats.empleadosPendientes.forEach(evaluacion => {
      const departamento = evaluacion.departamento || 'Sin Área';
      if (!evaluacionesPorDepartamento[departamento]) {
        evaluacionesPorDepartamento[departamento] = [];
      }
      evaluacionesPorDepartamento[departamento].push(evaluacion);
    });

    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h2 style={{ color: '#1e293b', fontSize: '1.5rem', margin: '0 0 0.5rem 0' }}>
            ⏳ Evaluaciones Pendientes
          </h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: '1rem' }}>
            {stats.pendientes} evaluaciones pendientes de {stats.totalEvaluaciones} totales
          </p>
        </div>

        {Object.keys(evaluacionesPorDepartamento).length > 0 ? (
          Object.entries(evaluacionesPorDepartamento).map(([departamento, evaluacionesDept]) => (
            <div key={departamento} style={{ marginBottom: '2rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '12px 12px 0 0',
                marginBottom: '1rem'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                  🏢 {departamento} ({evaluacionesDept.length} evaluaciones)
                </h3>
              </div>

              <div style={{ 
                display: 'grid', 
                gap: '1rem',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))'
              }}>
                {evaluacionesDept.map(evaluacion => {
                  const fechaInicio = evaluacion.fechaInicio ? new Date(evaluacion.fechaInicio.seconds * 1000) : null;
                  const fechaFin = evaluacion.fechaFin ? new Date(evaluacion.fechaFin.seconds * 1000) : null;
                  
                  let diasRestantes = null;
                  let estadoUrgencia = 'normal';
                  
                  if (fechaFin) {
                    const hoy = new Date();
                    diasRestantes = Math.ceil((fechaFin - hoy) / (1000 * 60 * 60 * 24));
                    
                    if (diasRestantes < 0) {
                      estadoUrgencia = 'vencida';
                    } else if (diasRestantes <= 3) {
                      estadoUrgencia = 'urgente';
                    }
                  }
                  
                  return (
                    <div key={evaluacion.id} style={{
                      background: 'white',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                      border: `2px solid ${estadoUrgencia === 'vencida' ? '#ef4444' : estadoUrgencia === 'urgente' ? '#f59e0b' : '#e5e7eb'}`,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Indicador de estado */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: estadoUrgencia === 'vencida' ? '#ef4444' : estadoUrgencia === 'urgente' ? '#f59e0b' : '#6b7280',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '0 12px 0 12px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        {estadoUrgencia === 'vencida' ? '🚨 VENCIDA' : estadoUrgencia === 'urgente' ? '⚠️ URGENTE' : '⏳ PENDIENTE'}
                      </div>

                      <div style={{ paddingTop: '1rem' }}>
                        <h4 style={{ margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.1rem' }}>
                          👤 {evaluacion.empleadoNombre}
                        </h4>
                        
                        <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>📋 Evaluación:</span>
                            <span style={{ fontWeight: '500', color: '#1e293b' }}>
                              {evaluacion.evaluacionNombre || 'No especificado'}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>🆔 Cédula:</span>
                            <span style={{ fontWeight: '500', color: '#1e293b' }}>
                              {evaluacion.empleadoCedula}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>🏢 Área:</span>
                            <span style={{ fontWeight: '500', color: '#3b82f6' }}>
                              {evaluacion.departamento}
                            </span>
                          </div>
                          
                          {fechaInicio && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#64748b' }}>📅 Inicio:</span>
                              <span style={{ fontWeight: '500', color: '#64748b' }}>
                                {fechaInicio.toLocaleDateString('es-ES')}
                              </span>
                            </div>
                          )}
                          
                          {fechaFin && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#64748b' }}>⏰ Límite:</span>
                                <span style={{ 
                                  fontWeight: '500', 
                                  color: estadoUrgencia === 'vencida' ? '#ef4444' : estadoUrgencia === 'urgente' ? '#f59e0b' : '#64748b'
                                }}>
                                  {fechaFin.toLocaleDateString('es-ES')}
                                </span>
                              </div>
                              
                              {diasRestantes !== null && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#64748b' }}>⏳ Días restantes:</span>
                                  <span style={{ 
                                    fontWeight: 'bold',
                                    color: estadoUrgencia === 'vencida' ? '#ef4444' : estadoUrgencia === 'urgente' ? '#f59e0b' : '#22c55e'
                                  }}>
                                    {diasRestantes < 0 ? `${Math.abs(diasRestantes)} días vencida` : `${diasRestantes} días`}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div style={{
            background: 'white',
            padding: '3rem',
            borderRadius: '16px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#374151' }}>
              ¡Excelente! No hay evaluaciones pendientes
            </h3>
            <p style={{ fontSize: '1rem' }}>
              Todas las evaluaciones han sido completadas exitosamente
            </p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #1e40af', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}></div>
          <p style={{ color: '#64748b', margin: 0 }}>Cargando datos del dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* CSS para animaciones */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* Header */}
      <header style={{
        backgroundColor: '#1e40af',
        color: 'white',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
            📊 Dashboard RRHH
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.875rem' }}>
            {userData ? userData.nombre : 'Usuario RRHH'} - {userData ? (userData.empresaNombre || userData.empresa || 'Sin empresa') : ''}
          </span>
          <button
            onClick={() => {
              auth.signOut();
              localStorage.clear();
              window.location.reload();
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            🚪 Cerrar Sesión
          </button>
        </div>
      </header>

      {/* Navegación por pestañas */}
      <nav style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 2rem'
      }}>
        <div style={{ display: 'flex', gap: '2rem' }}>
          {[
            { key: 'dashboard', label: '📊 Dashboard', icon: '📊' },
            { key: 'completadas', label: `✅ Completadas (${stats.completadas})`, icon: '✅' },
            { key: 'pendientes', label: `⏳ Pendientes (${stats.pendientes})`, icon: '⏳' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: activeTab === tab.key ? '#1e40af' : 'transparent',
                color: activeTab === tab.key ? 'white' : '#64748b',
                border: 'none',
                padding: '1rem 1.5rem',
                borderRadius: activeTab === tab.key ? '8px 8px 0 0' : '0',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                transition: 'all 0.2s',
                marginTop: activeTab === tab.key ? '0' : '4px'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Contenido de pestañas */}
      <main style={{ padding: '2rem' }}>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'completadas' && renderCompletadas()}
        {activeTab === 'pendientes' && renderPendientes()}
      </main>

      {/* Panel de debug */}
      {userData && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          fontSize: '0.875rem',
          maxWidth: '300px'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>🔍 Debug Panel</h4>
          <p style={{ margin: '0.25rem 0', color: '#6b7280' }}>
            <strong>Usuario:</strong> {userData.nombre}
          </p>
          <p style={{ margin: '0.25rem 0', color: '#6b7280' }}>
            <strong>Empresa:</strong> {userData.empresa_id || userData.empresaId || userData.empresa || 'No encontrada'}
          </p>
          <p style={{ margin: '0.25rem 0', color: '#6b7280' }}>
            <strong>Evaluaciones:</strong> {evaluaciones.length}
          </p>
          <p style={{ margin: '0.25rem 0', color: '#6b7280' }}>
            <strong>Empleados:</strong> {empleados.length}
          </p>
        </div>
      )}
    </div>
  );
};

export default RRHHDashboard;
